import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { selectAppUser, selectAppLocale } from 'modules/App/store/selectors'
import defaultNavigationConfig from 'constants/navigation'
import { useBreakpoint } from 'utils/hoc/breakpointProvider'
import Header from 'components/Header'
import Drawer from 'components/Drawer'
import GridSpinner from 'components/UI/GridSpinner'
import LayoutSuspense from 'layouts/LayoutSuspense'
import { ensureAuthorizedShellI18nLoaded } from 'i18nProduct'
import styles from './main-view-layout.module.scss'
import clsx from 'clsx'

const MainLayout = ({
  children = null,
  navItems,
  navLoading = false,
  className = '',
  collapsible = true,
  hasBalance = true,
  header = null,
  footer,
}) => {
  const user = useSelector(selectAppUser) ?? {}
  const locale = useSelector(selectAppLocale)
  const config = defaultNavigationConfig({ user })
  const { xl } = useBreakpoint()
  const [shellI18nReady, setShellI18nReady] = useState(!user?.id)
  // Explicit navItems (e.g. FAQ) always get a Drawer — don't gate on default cabinet nav.
  const showDrawer = Array.isArray(navItems) || config.length > 0

  useEffect(() => {
    if (!user?.id) {
      setShellI18nReady(true)
      return
    }

    let mounted = true
    setShellI18nReady(false)

    ensureAuthorizedShellI18nLoaded({ locale }).then(() => {
      if (mounted) {
        setShellI18nReady(true)
      }
    })

    return () => {
      mounted = false
    }
  }, [user?.id, locale])

  return (
    <div
      className={clsx(styles['main-wrapper'], className, {
        [styles['main-wrapper__with-header']]: !xl,
      })}
    >
      {!xl && <div className={styles['header-container']}>{header ?? <Header />}</div>}
      {user?.id && !shellI18nReady ? (
        <GridSpinner />
      ) : showDrawer ? (
        <Drawer
          collapsible={collapsible}
          footer={footer}
          hasBalance={hasBalance}
          navItems={navItems}
          navLoading={navLoading}
        >
          <LayoutSuspense>{children}</LayoutSuspense>
        </Drawer>
      ) : (
        <LayoutSuspense>{children}</LayoutSuspense>
      )}
    </div>
  )
}

MainLayout.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.element,
    PropTypes.oneOf([null]),
  ]),
  navItems: PropTypes.array,
  navLoading: PropTypes.bool,
  className: PropTypes.string,
  collapsible: PropTypes.bool,
  hasBalance: PropTypes.bool,
  header: PropTypes.node,
  footer: PropTypes.node,
}

export default MainLayout
